import { FIELD_TYPES } from '@wix/form-fields/constants';
/**
 * Geometry for the `Suspense` placeholder a form slot shows while the React-Aria field chunks load.
 *
 * Every entry of `FORM_FIELDS_RAC` is a `React.lazy` wrapper and there is no `Suspense` boundary
 * anywhere inside `@wix/form-fields` or `@wix/form-viewer`, so the host owns one. A flat box would
 * move the page when the real form lands, so the placeholder mirrors the row structure Forms will
 * lay out: `Grid` renders one flex child per distinct `item.row`, with
 * `FORMS_DEFAULT_GAP_PX` between them.
 *
 * Row heights are estimates — `Grid` sizes rows by content and the schema's `height` is not used for
 * layout — but a right-sized skeleton is the difference between a barely visible settle and a
 * several-hundred-pixel jump.
 *
 * Derived from `form` and `breakpoint` alone, both of which are identical on the server and on the
 * first client render, so the markup React hydrates against matches what the server emitted.
 */
/** Heights, in CSS pixels, that the RAC skin lands on per field type. */
const ROW_HEIGHT = {
    [FIELD_TYPES.CHECKBOX]: 24,
    [FIELD_TYPES.DEXT_CHECKBOX]: 24,
    [FIELD_TYPES.CONTACTS_SUBSCRIBE]: 24,
    [FIELD_TYPES.ECOM_SUBSCRIPTION]: 24,
    [FIELD_TYPES.SUBMIT_BUTTON]: 44,
    [FIELD_TYPES.HEADER]: 64,
    [FIELD_TYPES.RICH_TEXT]: 48,
    [FIELD_TYPES.TEXT_AREA]: 120,
    [FIELD_TYPES.CHECKBOX_GROUP]: 96,
    [FIELD_TYPES.RADIO_GROUP]: 96,
    [FIELD_TYPES.IMAGE_CHOICE]: 160,
};
/** Label + control + error slot, the shape of every single-line field in the skin. */
const DEFAULT_ROW_HEIGHT = 76;
const fieldTypeOf = (field) => field.view?.fieldType || field.identifier || undefined;
/** Flattens grouped items, keeping each one's own row. */
const flattenItems = (items, into = []) => {
    items?.forEach((item) => {
        if (item.fieldId) {
            into.push(item);
        }
        flattenItems(item.group?.items, into);
    });
    return into;
};
/**
 * Heights of the rows the viewer will lay out for the form's first renderable step — the only step
 * that mounts before a reader advances, and therefore all the placeholder has to cover.
 *
 * A row is as tall as its tallest field, matching `Grid`, which puts every item sharing a `row` in
 * one flex child.
 */
export const getFormPlaceholderRows = (form) => {
    if (!form) {
        return [];
    }
    const byId = new Map();
    [...(form.fields ?? []), ...(form.formFields ?? [])].forEach((field) => {
        const typed = field;
        if (typed.id) {
            byId.set(typed.id, { ...byId.get(typed.id), ...typed });
        }
    });
    const step = (form.steps ?? []).find((candidate) => candidate.layout?.large?.items?.length);
    if (!step) {
        return [];
    }
    const heightByRow = new Map();
    flattenItems(step.layout?.large?.items).forEach((item) => {
        if (item.row === undefined || item.row === null) {
            return; // Not rendered — see `isPlaced` in `field-support.ts`.
        }
        const field = item.fieldId ? byId.get(item.fieldId) : undefined;
        if (!field || field.hidden) {
            return;
        }
        const fieldType = fieldTypeOf(field);
        const height = (fieldType && ROW_HEIGHT[fieldType]) || DEFAULT_ROW_HEIGHT;
        heightByRow.set(item.row, Math.max(heightByRow.get(item.row) ?? 0, height));
    });
    return [...heightByRow.entries()].sort(([a], [b]) => a - b).map(([, height]) => height);
};
//# sourceMappingURL=form-placeholder.js.map